import { OverflowList } from '@theme/critical';
import VariantPicker from '@theme/variant-picker';
import { Component } from '@theme/component';
import { debounce, isDesktopBreakpoint, mediaQueryLarge, requestYieldCallback } from '@theme/utilities';
import { ThemeEvents, VariantSelectedEvent, VariantUpdateEvent, SlideshowSelectEvent } from '@theme/events';
import { morph } from '@theme/morph';

/**
 * A custom element that displays a product card.
 *
 * @typedef {object} Refs
 * @property {HTMLAnchorElement} productCardLink - The product card link element.
 * @property {import('slideshow').Slideshow} [slideshow] - The slideshow component.
 * @property {import('quick-add').QuickAddComponent} [quickAdd] - The quick add component.
 * @property {HTMLElement} [cardGallery] - The card gallery component.
 *
 * @extends {Component<Refs>}
 */
export class ProductCard extends Component {
  requiredRefs = ['productCardLink'];

  get productPageUrl() {
    return this.refs.productCardLink.href;
  }

   #isHoverPreview = false;

   /** @type {HTMLElement|null} */ #hoverLabel = null;
/** @type {number|null} */ #hoverOutTimer = null;


get #wrappers() {
  const baseWrap   = this.querySelector('[data-gallery="base"]');
  const extrasWrap = this.querySelector('[data-gallery="extras"]');
  const baseSS     = baseWrap?.querySelector('slideshow, slideshow-component');
  const extrasSS   = extrasWrap?.querySelector('slideshow, slideshow-component');
  return { baseWrap, extrasWrap, baseSS, extrasSS };
}

#showBase() {
  const { baseWrap, extrasWrap, baseSS } = this.#wrappers;

  if (baseWrap) { baseWrap.hidden = false; baseWrap.removeAttribute('hidden'); }
  if (extrasWrap) { extrasWrap.hidden = true; extrasWrap.setAttribute('hidden',''); }

  const selectFirst = (ss) => {
    const first = baseWrap?.querySelector?.('slideshow-slide:not([hidden])');
    const id = first?.getAttribute('slide-id');
    if (!id) return false;
    if (ss && typeof ss.select === 'function') {
      ss.select({ id }, undefined, { animate: false });
      return true;
    }
    return false;
  };

  if (baseSS) {
    if (!selectFirst(baseSS)) {
      // Try again when custom element upgrades
      customElements.whenDefined('slideshow-component').then(() => {
        const { baseSS: ss2 } = this.#wrappers;
        if (ss2) selectFirst(ss2);
      });
      requestAnimationFrame(() => {
        const { baseSS: ss3 } = this.#wrappers;
        if (ss3) selectFirst(ss3);
      });
    }
  } else {
    requestAnimationFrame(() => {
      const { baseSS: ss2 } = this.#wrappers;
      if (ss2 && !selectFirst(ss2)) {
        customElements.whenDefined('slideshow-component').then(() => {
          const { baseSS: ss3 } = this.#wrappers;
          if (ss3) selectFirst(ss3);
        });
      }
      else setTimeout(() => {
        const { baseSS: ss3 } = this.#wrappers;
        if (ss3) selectFirst(ss3);
      }, 16);
    });
  }
  requestAnimationFrame(() => window.dispatchEvent(new Event('resize')));
}

#restoreExtras() {
    const { baseWrap, extrasWrap, baseSS } = this.#wrappers;
if (extrasWrap) { extrasWrap.hidden = false; extrasWrap.removeAttribute('hidden'); }
if (baseWrap) { baseWrap.hidden = true; baseWrap.setAttribute('hidden',''); }
}


#showExtrasFor(variantIdRaw) {
  const { baseWrap, extrasWrap } = this.#wrappers;
  if (!extrasWrap) return this.#showBase();

  const norm = (v) => (String(v || '').match(/(\d+)(?!.*\d)/)?.[1] || String(v || ''));
  const vid = norm(variantIdRaw);

  // 1) Filtra slides por variant
  let found = 0;
  extrasWrap.querySelectorAll('slideshow-slide[data-extra-media]').forEach((el) => {
    const forNum = norm(el.getAttribute('data-extra-for-variant'));
    const forGid = norm(el.getAttribute('data-extra-for-variant-gid'));
    const match = (forNum && forNum === vid) || (forGid && forGid === vid);
    if (match) {
      el.removeAttribute('hidden');
      el.setAttribute('aria-hidden', 'false');
      found++;
    } else {
      el.setAttribute('hidden', '');
      el.setAttribute('aria-hidden', 'true');
    }
  });

  // 2) Si hay media, fuerza visibilidad de EXTRAS inmediatamente (aunque aún no haya instancia)
  if (found > 0) {
    baseWrap?.setAttribute('hidden', '');
    extrasWrap.hidden = false;
    extrasWrap.removeAttribute('hidden');

    // 3) Selecciona la primera visible cuando el slideshow esté listo (con reintentos)
    const selectFirst = (ss) => {
      const first = extrasWrap.querySelector('slideshow-slide:not([hidden])');
      const id = first?.getAttribute('slide-id');
      if (!id) return false;
      if (ss && typeof ss.select === 'function') {
        ss.select({ id }, undefined, { animate: false });
        return true;
      }
      return false;
    };

    const trySelect = () => {
      const { extrasSS } = this.#wrappers; // vuelve a resolver, quizá ya montó
      if (!extrasSS) return false;
      return selectFirst(extrasSS);
    };

    if (!trySelect()) {
      requestAnimationFrame(() => {
        if (!trySelect()) {
          customElements.whenDefined('slideshow-component').then(() => { trySelect(); });
          setTimeout(trySelect, 16);
        }
      });
    }
  } else {
    // Si no hay extras para ese variant, mejor sí mostramos BASE
    this.#showBase();
  }

  requestAnimationFrame(() => window.dispatchEvent(new Event('resize')));
}

  #onDelegatedOver = (e) => {
  // Soporta pointerover y mouseover
  const t = e.target;
  if (!(t instanceof Element)) return;
  const label = t.closest('.variant-option__button-label');
  if (!label || !this.contains(label)) return;

  // evita re-disparar si seguimos en el mismo label
  if (this.#hoverLabel === label) return;
  this.#hoverLabel = label;

  // cancela cualquier reset pendiente
  if (this.#hoverOutTimer) { clearTimeout(this.#hoverOutTimer); this.#hoverOutTimer = null; }

  // id robusto: data-media-id del label o el data-option-media-id del input
  const id =
    label.getAttribute('data-media-id') ||
    label.querySelector('input')?.getAttribute('data-option-media-id');

  if (id) this.previewVariant(id);
};

#onDelegatedOut = (e) => {
  const from = e.target instanceof Element ? e.target.closest('.variant-option__button-label') : null;
  const to   = e.relatedTarget instanceof Element ? e.relatedTarget.closest('.variant-option__button-label') : null;

  if (from && from === this.#hoverLabel && to !== from) {
    if (this.#hoverOutTimer) clearTimeout(this.#hoverOutTimer);
    this.#hoverOutTimer = window.setTimeout(() => {
      const anyHovered = !!this.querySelector('.variant-option__button-label:hover');
      if (!anyHovered) this.#restoreExtras();    
      this.#hoverOutTimer = null;
      this.#hoverLabel = null;
    }, 90);
  }
};

  /**
   * Gets the currently selected variant ID from the product card
   * @returns {string | null} The variant ID or null if none selected
   */
  getSelectedVariantId() {
    const checkedInput = /** @type {HTMLInputElement | null} */ (
      this.querySelector('input[type="radio"]:checked[data-variant-id]')
    );

    return checkedInput?.dataset.variantId || null;
  }

  /**
   * Gets the product card link element
   * @returns {HTMLAnchorElement | null} The product card link or null
   */
  getProductCardLink() {
    return this.refs.productCardLink || null;
  }

  #fetchProductPageHandler = () => {
    this.refs.quickAdd?.fetchProductPage(this.productPageUrl);
  };

  /**
   * Navigates to a URL link. Respects modifier keys for opening in new tab/window.
   * @param {Event} event - The event that triggered the navigation.
   * @param {URL} url - The URL to navigate to.
   */
  #navigateToURL = (event, url) => {
    // Check for modifier keys that should open in new tab/window (only for mouse events)
    const shouldOpenInNewTab =
      event instanceof MouseEvent && (event.metaKey || event.ctrlKey || event.shiftKey || event.button === 1);

    if (shouldOpenInNewTab) {
      event.preventDefault();
      window.open(url.href, '_blank');
      return;
    } else {
      window.location.href = url.href;
    }
  };

  /** @type {MutationObserver | null} */ #galleryObserver = null;
  /** @type {number | null} */ #galleryInitRaf = null;

  connectedCallback() {
    super.connectedCallback();

    const link = this.refs.productCardLink;
    if (!(link instanceof HTMLAnchorElement)) throw new Error('Product card link not found');
    this.#bindSwatchHover();
    this.#handleQuickAdd();

    this.dispatchEvent(
      new CustomEvent('variant:update', {
        bubbles: true,
        detail: { id: this.getSelectedVariantId() },
      })
    );

    this.addEventListener(ThemeEvents.variantUpdate, this.#handleVariantUpdate);
    this.addEventListener(ThemeEvents.variantSelected, this.#handleVariantSelected);
    this.addEventListener(SlideshowSelectEvent.eventName, this.#handleSlideshowSelect);
    mediaQueryLarge.addEventListener('change', this.#handleQuickAdd);

    this.addEventListener('click', this.navigateToProduct);

    this.addEventListener('pointerover', this.#onDelegatedOver);
    this.addEventListener('pointerout',  this.#onDelegatedOut);
    // Fallback por si algún navegador no dispara pointer*
    this.addEventListener('mouseover', this.#onDelegatedOver);
    this.addEventListener('mouseout',  this.#onDelegatedOut);

    // Initialize gallery state based on current/initial variant
    this.#initGallery();

    // Observe subtree changes (e.g., after filters re-render) to re-init gallery
    this.#galleryObserver?.disconnect();
    this.#galleryObserver = new MutationObserver((mutations) => {
      let shouldInit = false;
      for (const m of mutations) {
        if (m.type === 'childList') {
          for (const n of m.addedNodes) {
            if (n instanceof Element && (n.matches?.('.card-gallery') || n.closest?.('.card-gallery'))) {
              shouldInit = true; break;
            }
          }
        }
        if (shouldInit) break;
      }
      if (shouldInit) {
        if (this.#galleryInitRaf) cancelAnimationFrame(this.#galleryInitRaf);
        this.#galleryInitRaf = requestAnimationFrame(() => { this.#initGallery(); this.#galleryInitRaf = null; });
      }
    });
    this.#galleryObserver.observe(this, { childList: true, subtree: true });

    // Preload the next image on the slideshow to avoid white flashes on previewImage
    setTimeout(() => {
      if (this.refs.slideshow?.isNested) {
        this.#preloadNextPreviewImage();
      }
    });
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.removeEventListener('click', this.navigateToProduct);
    this.#galleryObserver?.disconnect();
    this.#galleryObserver = null;
  }

  /**
   * Called after Section Rendering morph updates this component's subtree.
   * Rebinds swatch hovers and re-initializes gallery selection for the current variant.
   */
  updatedCallback() {
    super.updatedCallback();
    this.#bindSwatchHover();
    this.#initGallery();
  }

// (Opcional) Si usas este hook, haz que mire al slideshow “activo”
  #preloadNextPreviewImage() {
    const { baseWrap, baseSS, extrasWrap, extrasSS } = this.#wrappers;
    const ss = extrasWrap && !extrasWrap.hasAttribute('hidden') ? extrasSS : baseSS;
    const current = ss?.slides?.[ss?.current];
    current?.nextElementSibling?.querySelector('img[loading="lazy"]')?.removeAttribute('loading');
  }

  /**
   * Resolve initial/current variant id for gallery init.
   * Priority: checked radio with data-variant-id -> checked with data-first-available-or-first-variant-id ->
   * card gallery data-initial-variant-id -> variant param in product link URL.
   * @returns {string|null}
   */
  #resolveInitialVariantId() {
    const checkedWithVariant = /** @type {HTMLInputElement|null} */ (
      this.querySelector('input[type="radio"][data-variant-id]:checked')
    );
    if (checkedWithVariant?.dataset.variantId) return checkedWithVariant.dataset.variantId;

    const checkedWithFallback = /** @type {HTMLInputElement|null} */ (
      this.querySelector('input[type="radio"][data-first-available-or-first-variant-id]:checked')
    );
    if (checkedWithFallback?.dataset.firstAvailableOrFirstVariantId)
      return checkedWithFallback.dataset.firstAvailableOrFirstVariantId;

    const initialFromDom = this.querySelector('.card-gallery')?.getAttribute('data-initial-variant-id');
    if (initialFromDom) return initialFromDom;

    try {
      const url = new URL(this.productPageUrl);
      const q = url.searchParams.get('variant');
      if (q) return q;
    } catch {}

    return null;
  }

  /**
   * Initializes the gallery selection for the current/initial variant, with graceful fallback.
   */
  #initGallery() {
    const vid = this.#resolveInitialVariantId();
    if (vid) {
      this.#showExtrasFor(vid);
    } else {
      this.#showBase();
    }
  }

  /**
   * Handles the quick add event.
   */
  #handleQuickAdd = () => {
    this.removeEventListener('pointerenter', this.#fetchProductPageHandler);
    this.removeEventListener('focusin', this.#fetchProductPageHandler);

    if (isDesktopBreakpoint()) {
      this.addEventListener('pointerenter', this.#fetchProductPageHandler);
      this.addEventListener('focusin', this.#fetchProductPageHandler);
    }
  };

  /**
   * Handles the variant selected event.
   * @param {VariantSelectedEvent} event - The variant selected event.
   */
  #handleVariantSelected = (event) => {
    if (event.target !== this.variantPicker) {
      this.variantPicker?.updateSelectedOption(event.detail.resource.id);
    }
  };

  /**
   * Handles the variant update event.
   * Updates price, checks for unavailable variants, and updates product URL.
   * @param {VariantUpdateEvent} event - The variant update event.
   */
  #handleVariantUpdate = (event) => {
    // Stop the event from bubbling up to the section, variant updates triggered from product cards are fully handled
    // by this component and should not affect anything outside the card.
    event.stopPropagation();
    // Ignore lightweight variant:update events without data payload
    if (!event?.detail?.data) return;

    this.updatePrice(event);
    this.#isUnavailableVariantSelected(event);
    this.#updateProductUrl(event);
    this.refs.quickAdd?.fetchProductPage(this.productPageUrl);

    if (event.target !== this.variantPicker) {
      this.variantPicker?.updateVariantPicker(event.detail.data.html);
    }
    this.#bindSwatchHover(); 

    this.#updateVariantImages();

    // Ensure card gallery switches to the variant's extras carousel on PDP recommendations too
    if (event.detail?.resource?.id) {
      this.#showExtrasFor(event.detail.resource.id);
    }
    this.#previousSlideIndex = null;

    // Remove attribute after re-rendering since a variant selection has been made
    this.removeAttribute('data-no-swatch-selected');

    // Force overflow list to reflow after variant update
    // This fixes an issue where the overflow counter doesn't update properly in some browsers
    this.#updateOverflowList();
    this.dispatchEvent(
      new CustomEvent('variant:update', {
        bubbles: true,
        detail: { id: this.getSelectedVariantId() },
      })
    );
  };

  /**
   * Forces the overflow list to recalculate by dispatching a reflow event.
   * This ensures the overflow counter displays correctly after variant updates.
   */
  #updateOverflowList() {
    // Find the overflow list in the variant picker
    const overflowList = this.querySelector('swatches-variant-picker-component overflow-list');
    const isActiveOverflowList = overflowList?.querySelector('[slot="overflow"]') ? true : false;
    if (!overflowList || !isActiveOverflowList) return;

    // Use requestAnimationFrame to ensure DOM has been updated
    requestAnimationFrame(() => {
      // Dispatch a reflow event to trigger recalculation
      overflowList.dispatchEvent(
        new CustomEvent('reflow', {
          bubbles: true,
          detail: {},
        })
      );
    });
  }

  /**
   * Updates the DOM with a new price.
   * @param {VariantUpdateEvent} event - The variant update event.
   */
  updatePrice(event) {
    const priceContainer = this.querySelectorAll(`product-price [ref='priceContainer']`)[1];
    const newPriceElement = event.detail.data.html.querySelector(`product-price [ref='priceContainer']`);

    if (newPriceElement && priceContainer) {
      morph(priceContainer, newPriceElement);
    }
  }

  /**
   * Updates the product URL based on the variant update event.
   * @param {VariantUpdateEvent} event - The variant update event.
   */
  #updateProductUrl(event) {
    const anchorElement = event.detail.data.html?.querySelector('product-card a');
    const featuredMediaUrl = event.detail.data.html
      ?.querySelector('product-card-link')
      ?.getAttribute('data-featured-media-url');

    // If the product card is inside a product link, update the product link's featured media URL
    if (featuredMediaUrl && this.closest('product-card-link'))
      this.closest('product-card-link')?.setAttribute('data-featured-media-url', featuredMediaUrl);

    if (anchorElement instanceof HTMLAnchorElement) {
      // If the href is empty, don't update the product URL eg: unavailable variant
      if (anchorElement.getAttribute('href')?.trim() === '') return;

      const productUrl = anchorElement.href;
      const { productCardLink, productTitleLink, cardGalleryLink } = this.refs;

      productCardLink.href = productUrl;
      if (cardGalleryLink instanceof HTMLAnchorElement) {
        cardGalleryLink.href = productUrl;
      }
      if (productTitleLink instanceof HTMLAnchorElement) {
        productTitleLink.href = productUrl;
      }
    }
  }

  /**
   * Checks if an unavailable variant is selected.
   * @param {VariantUpdateEvent} event - The variant update event.
   */
  #isUnavailableVariantSelected(event) {
    const allVariants = /** @type {NodeListOf<HTMLInputElement>} */ (
      event.detail.data.html.querySelectorAll('input:checked')
    );

    for (const variant of allVariants) {
      this.#toggleAddToCartButton(variant.dataset.optionAvailable === 'true');
    }
  }

  #isAnySwatchHovered() {
    return !!this.querySelector('.variant-option__button-label:hover');
  }

  /**
   * Toggles the add to cart button state.
   * @param {boolean} enable - Whether to enable or disable the button.
   */
  #toggleAddToCartButton(enable) {
    const addToCartButton = this.querySelector('.add-to-cart__button button');

    if (addToCartButton instanceof HTMLButtonElement) {
      addToCartButton.disabled = !enable;
    }
  }

  /**
   * Hide the variant images that are not for the selected variant.
   */
// En #updateVariantImages, no ocultes nada si extras está activo (no hay [variant-image] ahí):
  #updateVariantImages() {
    const { baseWrap, baseSS, extrasWrap } = this.#wrappers;
    const extrasIsVisible = extrasWrap && !extrasWrap.hasAttribute('hidden');
    if (extrasIsVisible) return; // no tocar cuando estás en extras

    if (!this.variantPicker?.selectedOption || !baseSS || typeof baseSS.select !== 'function') return;
    const selectedImageId = this.variantPicker?.selectedOption.dataset.optionMediaId;
    if (!selectedImageId) return;

    for (const slide of baseSS.slides || []) {
      if (slide.getAttribute('variant-image') == null) continue;
      slide.hidden = slide.getAttribute('slide-id') !== selectedImageId;
    }
    baseSS.select({ id: selectedImageId }, undefined, { animate: false });
  }

  /**
   * Gets all variant inputs.
   * @returns {NodeListOf<HTMLInputElement>} All variant input elements.
   */
  get allVariants() {
    return this.querySelectorAll('input[data-variant-id]');
  }

  /**
   * Gets the variant picker component.
   * @returns {VariantPicker | null} The variant picker component.
   */
  get variantPicker() {
    return this.querySelector('swatches-variant-picker-component');
  }
  /** @type {number | null} */
  #previousSlideIndex = null;

  /**
   * Handles the slideshow select event.
   * @param {SlideshowSelectEvent} event - The slideshow select event.
   */
  #handleSlideshowSelect = (event) => {
    if (event.detail.userInitiated) {
      this.#previousSlideIndex = event.detail.index;
    }
  };

  #onSwatchEnter = (e) => {
  if (this.#hoverOutTimer) { clearTimeout(this.#hoverOutTimer); this.#hoverOutTimer = null; }
  const el = /** @type {HTMLElement} */ (e.currentTarget);
  const id = el?.getAttribute('data-media-id');
  if (id) this.previewVariant(id);
};

// Usamos pointerout + pequeño delay: si entro a otro swatch, se cancela
#onSwatchLeave = () => {
  if (this.#hoverOutTimer) clearTimeout(this.#hoverOutTimer);
  this.#hoverOutTimer = window.setTimeout(() => {
    if (!this.#isAnySwatchHovered()) this.#restoreExtras();  // 👈
    this.#hoverOutTimer = null;
  }, 80);
};

#bindSwatchHover() {
  const labels = this.querySelectorAll('.variant-option__button-label');
  labels.forEach((el) => {
    el.removeEventListener('pointerenter', this.#onSwatchEnter);
    el.removeEventListener('pointerleave', this.#onSwatchLeave);
    el.removeEventListener('pointerout',   this.#onSwatchLeave);
    el.removeEventListener('mouseenter',   this.#onSwatchEnter);
    el.removeEventListener('mouseleave',   this.#onSwatchLeave);

    el.addEventListener('pointerenter', this.#onSwatchEnter);
    // pointerout burbujea y es más confiable al mover entre hijos del label
    el.addEventListener('pointerout',   this.#onSwatchLeave);

    // fallback
    el.addEventListener('mouseenter', this.#onSwatchEnter);
    el.addEventListener('mouseleave', this.#onSwatchLeave);
  });
}

  /**
   * Previews a variant.
   * @param {string} id - The id of the variant to preview.
   */
  /**
   * PREVIEW EN HOVER: ahora decide dinámicamente en qué carrusel previsualizar.
   * - Si “extras” está activo, cambiamos temporalmente a “base” para poder mostrar cualquier swatch.
   * - Seleccionamos la slide por media id (el que pasas en on:pointerenter).
   */
previewVariant(id) {
  const { extrasWrap } = this.#wrappers;
  const extrasIsVisible = extrasWrap && !extrasWrap.hasAttribute('hidden');

  if (extrasIsVisible) {
    this.#isHoverPreview = true;
    this.#showBase();
  } else {
    this.#isHoverPreview = false;
  }

  const trySelect = () => {
    const { baseSS } = this.#wrappers;
    if (!baseSS || typeof baseSS.select !== 'function') return false;
    this.resetVariant.cancel();
    baseSS.select({ id }, undefined, { animate: false });
    const current = baseSS.slides?.[baseSS.current];
    current?.nextElementSibling?.querySelector('img[loading="lazy"]')?.removeAttribute('loading');
    return true;
  };

  if (!trySelect()) {
    requestAnimationFrame(() => {
      if (!trySelect()) {
        customElements.whenDefined('slideshow-component').then(() => { trySelect(); });
        setTimeout(trySelect, 16);
      }
    });
  }
}


  /**
   * Previews the next image.
   * @param {PointerEvent} event - The pointer event.
   */
  previewImage(event) {
    if (event.pointerType !== 'mouse') return;

    const { slideshow } = this.refs;

    if (!slideshow || typeof slideshow.next !== 'function' || typeof slideshow.select !== 'function') return;

    this.resetVariant.cancel();

    if (this.#previousSlideIndex != null && this.#previousSlideIndex > 0) {
      slideshow.select(this.#previousSlideIndex, undefined, { animate: false });
    } else {
      slideshow.next(undefined, { animate: false });
      setTimeout(() => this.#preloadNextPreviewImage());
    }
  }

  /**
   * Resets the image to the variant image.
   * @param {PointerEvent} event - The pointer event.
   */
  resetImage(event) {
    if (event.pointerType !== 'mouse') return;

    const { slideshow } = this.refs;

    if (!this.variantPicker) {
      if (!slideshow || typeof slideshow.previous !== 'function') return;
      slideshow.previous(undefined, { animate: false });
    } else {
      this.#resetVariant();
    }
  }

  /**
   * Resets the image to the variant image.
   */
/**
   * RESTORE TRAS HOVER: si veníamos de “extras”, restáuralo para la variante seleccionada.
   */
#resetVariant = () => {
  const { extrasWrap } = this.#wrappers;

  if (this.#isHoverPreview) {
    // ID robusto: checked -> data-initial-variant-id -> URL ?variant=
    let vId =
      this.getSelectedVariantId() ||
      this.querySelector('.card-gallery')?.getAttribute('data-initial-variant-id') ||
      null;

    if (!vId) {
      try {
        const url = new URL(this.productPageUrl);
        vId = url.searchParams.get('variant');
      } catch {}
    }

    this.#showExtrasFor(vId);
    this.#isHoverPreview = false;
    return;
  }

  // Comportamiento anterior cuando no veníamos de hover
  const { baseSS } = this.#wrappers;
  if (!baseSS) return;

  if (this.variantPicker?.selectedOption) {
    const id = this.variantPicker.selectedOption.dataset.optionMediaId;
    if (id && typeof baseSS.select === 'function') { baseSS.select({ id }, undefined, { animate: false }); return; }
  }
  const initialSlide = baseSS.initialSlide;
  const slideId = initialSlide?.getAttribute('slide-id');
  if (initialSlide && baseSS.slides?.includes(initialSlide) && slideId && typeof baseSS.select === 'function') {
    baseSS.select({ id: slideId }, undefined, { animate: false });
    return;
  }
  if (typeof baseSS.previous === 'function') baseSS.previous(undefined, { animate: false });
};


  resetVariant = debounce(this.#resetVariant, 100);

  /**
   * Intercepts the click event on the product card anchor, we want
   * to use this to add an intermediate state to the history.
   * This intermediate state captures the page we were on so that we
   * navigate back to the same page when the user navigates back.
   * In addition to that, it captures the product card anchor so that we
   * have the specific product card in view.
   *
   * A product card can have other interactive elements like variant picker,
   * so we do not navigate if the click was on one of those elements.
   *
   * @param {Event} event
   */
  navigateToProduct = (event) => {
    if (!(event.target instanceof Element)) return;

    // Don't navigate if this product card is marked as no-navigation (e.g., in theme editor)
    if (this.hasAttribute('data-no-navigation')) return;

    const interactiveElement = event.target.closest('button, input, label, select, [tabindex="1"]');

    // If the click was on an interactive element, do nothing.
    if (interactiveElement) {
      return;
    }

    const link = this.refs.productCardLink;
    if (!link.href) return;
    const linkURL = new URL(link.href);

    const productCardAnchor = link.getAttribute('id');
    if (!productCardAnchor) return;

    const url = new URL(window.location.href);
    const parent = this.closest('li');
    url.hash = productCardAnchor;
    if (parent && parent.dataset.page) {
      url.searchParams.set('page', parent.dataset.page);
    }

    if (!window.Shopify.designMode) {
      requestYieldCallback(() => {
        history.replaceState({}, '', url.toString());
      });
    }

    const targetLink = event.target.closest('a');
    // Let the native navigation handle the click if it was on a link.
    if (!targetLink) {
      this.#navigateToURL(event, linkURL);
    }
  };

  // /**
  //  * Resets the variant.
  //  */
  // resetVariant = debounce(this.#resetVariant, 100);
}

if (!customElements.get('product-card')) {
  customElements.define('product-card', ProductCard);
}

/**
 * A custom element that displays a variant picker with swatches.
 *
 * @typedef {object} SwatchesRefs
 * @property {HTMLElement} overflowList
 *
 * @extends {VariantPicker<SwatchesRefs>}
 */
class SwatchesVariantPickerComponent extends VariantPicker {
  #keepOverflowExpanded = false;

  connectedCallback() {
    super.connectedCallback();

    // Cache the parent product card
    this.parentProductCard = this.closest('product-card');

    // Listen for variant updates to apply pending URL changes
    this.addEventListener(ThemeEvents.variantUpdate, this.#handleCardVariantUrlUpdate.bind(this));
  }

  /**
   * Updates the card URL when a variant is selected.
   */
  #handleCardVariantUrlUpdate() {
    if (this.pendingVariantId && this.parentProductCard instanceof ProductCard) {
      const currentUrl = new URL(this.parentProductCard.refs.productCardLink.href);
      currentUrl.searchParams.set('variant', this.pendingVariantId);
      this.parentProductCard.refs.productCardLink.href = currentUrl.toString();
      this.pendingVariantId = null;
    }
  }

  /**
   * Override the variantChanged method to handle unavailable swatches with available alternatives.
   * @param {Event} event - The variant change event.
   */
  variantChanged(event) {
    if (!(event.target instanceof HTMLElement)) return;

    // Check if this is a swatch input
    const isSwatchInput = event.target instanceof HTMLInputElement && event.target.name?.includes('-swatch');
    const clickedSwatch = event.target;
    const availableCount = parseInt(clickedSwatch.dataset.availableCount || '0');
    const firstAvailableVariantId = clickedSwatch.dataset.firstAvailableOrFirstVariantId;

    // For swatch inputs, check if we need special handling
    if (isSwatchInput && availableCount > 0 && firstAvailableVariantId) {
      // If this is an unavailable variant but there are available alternatives
      // Prevent the default handling
      event.stopPropagation();

      // Update the selected option visually
      this.updateSelectedOption(clickedSwatch);

      // Build request URL with the first available variant
      const productUrl = this.dataset.productUrl?.split('?')[0];

      if (!productUrl) return;

      const url = new URL(productUrl, window.location.origin);
      url.searchParams.set('variant', firstAvailableVariantId);
      url.searchParams.set('section_id', 'section-rendering-product-card');

      const requestUrl = url.href;

      // Store the variant ID we want to apply to the URL
      this.pendingVariantId = firstAvailableVariantId;

      // Use parent's fetch method
      this.fetchUpdatedSection(requestUrl);
      return;
    }

    // For all other cases, use the default behavior
    super.variantChanged(event);
  }

  /**
   * Shows all swatches.
   * @param {Event} [event] - The event that triggered the show all swatches.
   */
  showAllSwatches(event) {
    event?.preventDefault();

    const { overflowList } = this.refs;

    if (overflowList instanceof OverflowList) {
      overflowList.showAll();
      this.#keepOverflowExpanded = true;
    }
  }

  updateVariantPicker(newHtml) {
    const newProduct = super.updateVariantPicker(newHtml);

    if (this.#keepOverflowExpanded) {
      requestAnimationFrame(() => {
        const overflowLists = this.querySelectorAll('overflow-list');

        overflowLists.forEach((list) => {
          if (list instanceof OverflowList) {
            list.showAll();
          }
        });
      });
    }

    return newProduct;
  }
}

if (!customElements.get('swatches-variant-picker-component')) {
  customElements.define('swatches-variant-picker-component', SwatchesVariantPickerComponent);
}
